/**
 * O que o aluno decidiu sobre as alternativas, antes de responder.
 *
 * São dois gestos sobre a mesma lista - marcar a que ele acha certa e riscar as
 * que descartou -, e por isso um estado só, e não dois. A regra que os liga é
 * uma: **a alternativa escolhida nunca está riscada**. Marcar e riscar afirmam
 * coisas opostas sobre a mesma alternativa, e deixar as duas convivendo acenderia
 * o botão "Responder" sobre uma alternativa desenhada como descartada - a tela
 * dizendo duas coisas, e o aluno tendo que decidir em qual acreditar.
 *
 * O risco é rascunho e morre com a tela, de propósito. Ele imita o lápis na
 * prova impressa: serve durante a leitura, para estreitar o campo antes de
 * escolher, e não é resposta. O que o servidor guarda é o que o aluno
 * respondeu, não o caminho que ele fez até lá.
 */
export type Decisao = {
  /** A alternativa marcada, quando há. */
  readonly escolhida?: string;
  /** As alternativas descartadas, por letra. */
  readonly riscadas: readonly string[];
};

/** A questão ainda intocada. */
export const SEM_DECISAO: Decisao = { riscadas: [] };

/**
 * Risca a alternativa - ou desfaz o risco, se ela já estava riscada.
 *
 * Riscar a alternativa marcada desmarca: quem risca está dizendo que ela não
 * serve, e é o gesto mais recente que vale.
 */
export function riscar(decisao: Decisao, letra: string): Decisao {
  if (decisao.riscadas.includes(letra)) {
    return { ...decisao, riscadas: decisao.riscadas.filter((riscada) => riscada !== letra) };
  }

  return {
    escolhida: decisao.escolhida === letra ? undefined : decisao.escolhida,
    riscadas: [...decisao.riscadas, letra],
  };
}

/**
 * Marca a alternativa.
 *
 * Se ela estava riscada, o risco cai: o aluno mudou de ideia, e obrigá-lo a
 * desfazer o risco antes de poder marcar seria exigir dois gestos para uma
 * decisão só.
 */
export function escolher(decisao: Decisao, letra: string): Decisao {
  return { escolhida: letra, riscadas: decisao.riscadas.filter((riscada) => riscada !== letra) };
}
